import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import adminApi from '../../api/admin';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../api/index';

const PermissionManagement = () => {
  const { roleId } = useParams();
  const navigate = useNavigate();
  const { isSuperuser } = useAuth();
  
  const [role, setRole] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rolePermissions, setRolePermissions] = useState(null);
  const [availableSchemas, setAvailableSchemas] = useState([]);
  const [availableTables, setAvailableTables] = useState({});
  
  const [isAddSchemaModalOpen, setIsAddSchemaModalOpen] = useState(false);
  const [isAddTableModalOpen, setIsAddTableModalOpen] = useState(false);
  
  const [addSchemaForm, setAddSchemaForm] = useState({
    schema_name: '',
    permission: 'read'
  });
  
  const [addTableForm, setAddTableForm] = useState({
    schema_name: '',
    table_name: '',
    permission: 'read'
  });
  
  const [selectedSchema, setSelectedSchema] = useState('');
  
  useEffect(() => {
    // Show a loading message
    console.log('Permission Management component loaded for role ID:', roleId);
    
    // Check if user is authorized
    if (!isSuperuser()) {
      console.log('User is not a superuser, redirecting...');
      navigate('/admin/roles');
      return;
    }
    
    // Check if we have a valid roleId
    if (!roleId || isNaN(parseInt(roleId))) {
      console.error('Invalid role ID:', roleId);
      setError('Invalid role ID provided');
      setIsLoading(false);
      return;
    }
    
    const fetchRoleAndPermissions = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Get role details and permissions
        const roles = await adminApi.getRoles();
        const currentRole = roles.find(r => r.id === parseInt(roleId));
        
        if (!currentRole) {
          setError('Role not found');
          return;
        }
        
        setRole(currentRole);
        
        // Get role permissions
        try {
          console.log(`Fetching permissions for role ${roleId}...`);
          const permissions = await adminApi.getRolePermissions(roleId);
          console.log('Permissions received:', permissions);
          setRolePermissions(permissions);
        } catch (permError) {
          console.error('Error fetching role permissions:', permError);
          // Continue with empty permissions
          setRolePermissions({
            id: parseInt(roleId),
            name: currentRole.name,
            description: currentRole.description,
            permissions: {
              schema_permissions: [],
              table_permissions: []
            }
          });
        }
        
        // Fetch available database schemas
        try {
          const dbMetadata = await apiClient.get('/adss/v1/users/me/database');
          setAvailableSchemas(dbMetadata.data.schemas.map(schema => schema.name));
          
          // Prepare tables by schema
          const tablesBySchema = {};
          dbMetadata.data.schemas.forEach(schema => {
            tablesBySchema[schema.name] = schema.tables.map(table => table.name);
          });
          setAvailableTables(tablesBySchema);
        } catch (dbError) {
          console.error('Error fetching database metadata:', dbError);
          setAvailableSchemas([]);
          setAvailableTables({});
        }
        
      } catch (err) {
        console.error('Failed to fetch role data:', err);
        setError('Failed to load role data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchRoleAndPermissions();
  }, [roleId, navigate, isSuperuser]);
  
  const handleInputChange = (e, formSetter) => {
    const { name, value } = e.target;
    formSetter(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSchemaChange = (e) => {
    const schemaName = e.target.value;
    setSelectedSchema(schemaName);
    setAddTableForm(prev => ({
      ...prev,
      schema_name: schemaName,
      table_name: ''
    }));
  };
  
  const handleAddSchemaPermission = async (e) => {
    e.preventDefault();
    
    try {
      await adminApi.addSchemaPermission(roleId, addSchemaForm.schema_name, addSchemaForm.permission);
      
      // Refresh permissions
      const permissions = await adminApi.getRolePermissions(roleId);
      setRolePermissions(permissions);
      
      // Reset form and close modal
      setAddSchemaForm({ schema_name: '', permission: 'read' });
      setIsAddSchemaModalOpen(false);
    } catch (err) {
      console.error('Failed to add schema permission:', err);
    }
  };
  
  const handleAddTablePermission = async (e) => {
    e.preventDefault();
    
    try {
      await adminApi.addTablePermission(
        roleId, 
        addTableForm.schema_name, 
        addTableForm.table_name, 
        addTableForm.permission
      );
      
      // Refresh permissions
      const permissions = await adminApi.getRolePermissions(roleId);
      setRolePermissions(permissions);
      
      // Reset form and close modal
      setAddTableForm({ schema_name: '', table_name: '', permission: 'read' });
      setIsAddTableModalOpen(false);
    } catch (err) {
      console.error('Failed to add table permission:', err);
    }
  };
  
  const handleRemoveSchemaPermission = async (schemaName) => {
    try {
      await adminApi.removeSchemaPermission(roleId, schemaName);
      
      // Refresh permissions
      const permissions = await adminApi.getRolePermissions(roleId);
      setRolePermissions(permissions);
    } catch (err) {
      console.error('Failed to remove schema permission:', err);
    }
  };
  
  const handleRemoveTablePermission = async (schemaName, tableName) => {
    try {
      await adminApi.removeTablePermission(roleId, schemaName, tableName);
      
      // Refresh permissions
      const permissions = await adminApi.getRolePermissions(roleId);
      setRolePermissions(permissions);
    } catch (err) {
      console.error('Failed to remove table permission:', err);
    }
  };
  
  if (isLoading) {
    return (
      <div className="p-6 bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md">
        <div className="flex justify-between mb-4">
          <h2 className="text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">Permissions Management</h2>
          <button 
            onClick={() => navigate('/admin/roles')}
            className="px-3 py-1.5 text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-md hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background"
          >
            Back to Roles
          </button>
        </div>

        <div className="flex flex-col items-center justify-center p-8">
          <svg className="w-12 h-12 text-primary-600 dark:text-primary-400 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-4 text-ui-light-text-secondary dark:text-ui-dark-text-secondary">Loading permission data...</p>
          <p className="mt-2 text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">This may take a moment. If it takes too long, you can return to the roles list.</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="p-6 bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md">
        <div className="p-4 text-status-error-light-text dark:text-status-error-dark-text bg-status-error-light-bg dark:bg-status-error-dark-bg rounded-md border border-status-error-light-text/20 dark:border-status-error-dark-text/20">
          <p className="font-medium">Error:</p>
          <p>{error}</p>
        </div>
        <button 
          onClick={() => navigate('/admin/roles')}
          className="mt-4 px-4 py-2 text-sm font-medium text-white dark:text-ui-dark-text-primary bg-primary-600 dark:bg-primary-700 rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors"
        >
          Back to Roles
        </button>
      </div>
    );
  }
  
  if (!role || !rolePermissions) {
    return (
      <div className="p-6 bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md">
        <div className="p-4 text-status-warning-light-text dark:text-status-warning-dark-text bg-status-warning-light-bg dark:bg-status-warning-dark-bg rounded-md border border-status-warning-light-text/20 dark:border-status-warning-dark-text/20">
          <p className="font-medium">Note:</p>
          <p>Role information could not be loaded.</p>
        </div>
        <button 
          onClick={() => navigate('/admin/roles')}
          className="mt-4 px-4 py-2 text-sm font-medium text-white dark:text-ui-dark-text-primary bg-primary-600 dark:bg-primary-700 rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors"
        >
          Back to Roles
        </button>
      </div>
    );
  }
  
  return (
    <div className="p-6 bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary">
            Permissions for Role: {role.name}
          </h1>
          {role.description && (
            <p className="mt-1 text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">{role.description}</p>
          )}
        </div>
        <div>
          <button 
            onClick={() => navigate('/admin/roles')}
            className="px-4 py-2 text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-md hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background"
          >
            Back to Roles
          </button>
        </div>
      </div>
      
      {/* Schema Permissions Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">Schema Permissions</h2>
          <button
            onClick={() => setIsAddSchemaModalOpen(true)}
            className="px-3 py-1.5 text-sm font-medium text-white dark:text-ui-dark-text-primary bg-primary-600 dark:bg-primary-700 rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors"
          >
            Add Schema Permission
          </button>
        </div>
        
        {rolePermissions.permissions.schema_permissions.length > 0 ? (
          <div className="overflow-x-auto border border-ui-light-border dark:border-ui-dark-border rounded-md">
            <table className="min-w-full divide-y divide-ui-light-border dark:divide-ui-dark-border">
              <thead className="bg-ui-light-hover dark:bg-ui-dark-hover">
                <tr>
                  <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase">
                    Schema Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase">
                    Permission
                  </th>
                  <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-ui-light-card dark:bg-ui-dark-card divide-y divide-ui-light-border dark:divide-ui-dark-border">
                {rolePermissions.permissions.schema_permissions.map((perm, idx) => (
                  <tr key={idx} className="hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">
                        {perm.schema_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 text-xs font-semibold leading-5 rounded-full ${
                        perm.permission === 'all' ? 'bg-status-success-light-bg dark:bg-status-success-dark-bg text-status-success-light-text dark:text-status-success-dark-text' : 
                        perm.permission === 'write' ? 'bg-status-warning-light-bg dark:bg-status-warning-dark-bg text-status-warning-light-text dark:text-status-warning-dark-text' : 
                        'bg-status-info-light-bg dark:bg-status-info-dark-bg text-status-info-light-text dark:text-status-info-dark-text'
                      }`}>
                        {perm.permission}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium whitespace-nowrap">
                      <button
                        onClick={() => handleRemoveSchemaPermission(perm.schema_name)}
                        className="text-status-error-light-text dark:text-status-error-dark-text hover:text-status-error-light-text/80 dark:hover:text-status-error-dark-text/80 transition-colors"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-4 text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary bg-ui-light-hover dark:bg-ui-dark-hover border border-ui-light-border dark:border-ui-dark-border rounded-md">
            No schema permissions assigned to this role.
          </div>
        )}
      </div>
      
      {/* Table Permissions Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">Table Permissions</h2>
          <button
            onClick={() => setIsAddTableModalOpen(true)}
            className="px-3 py-1.5 text-sm font-medium text-white dark:text-ui-dark-text-primary bg-primary-600 dark:bg-primary-700 rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors"
          >
            Add Table Permission
          </button>
        </div>
        
        {rolePermissions.permissions.table_permissions.length > 0 ? (
          <div className="overflow-x-auto border border-ui-light-border dark:border-ui-dark-border rounded-md">
            <table className="min-w-full divide-y divide-ui-light-border dark:divide-ui-dark-border">
              <thead className="bg-ui-light-hover dark:bg-ui-dark-hover">
                <tr>
                  <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase">
                    Schema
                  </th>
                  <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase">
                    Table
                  </th>
                  <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase">
                    Permission
                  </th>
                  <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-ui-light-card dark:bg-ui-dark-card divide-y divide-ui-light-border dark:divide-ui-dark-border">
                {rolePermissions.permissions.table_permissions.map((perm, idx) => (
                  <tr key={idx} className="hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">
                        {perm.schema_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">
                        {perm.table_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 text-xs font-semibold leading-5 rounded-full ${
                        perm.permission === 'all' ? 'bg-status-success-light-bg dark:bg-status-success-dark-bg text-status-success-light-text dark:text-status-success-dark-text' : 
                        perm.permission === 'write' ? 'bg-status-warning-light-bg dark:bg-status-warning-dark-bg text-status-warning-light-text dark:text-status-warning-dark-text' : 
                        'bg-status-info-light-bg dark:bg-status-info-dark-bg text-status-info-light-text dark:text-status-info-dark-text'
                      }`}>
                        {perm.permission}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium whitespace-nowrap">
                      <button
                        onClick={() => handleRemoveTablePermission(perm.schema_name, perm.table_name)}
                        className="text-status-error-light-text dark:text-status-error-dark-text hover:text-status-error-light-text/80 dark:hover:text-status-error-dark-text/80 transition-colors"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-4 text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary bg-ui-light-hover dark:bg-ui-dark-hover border border-ui-light-border dark:border-ui-dark-border rounded-md">
            No table permissions assigned to this role.
          </div>
        )}
      </div>
      
      {/* Add Schema Permission Modal */}
      {isAddSchemaModalOpen && (
        <div className="fixed inset-0 z-10 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-ui-light-card dark:bg-ui-dark-card rounded-lg overflow-hidden shadow-xl w-full max-w-lg">
            <form onSubmit={handleAddSchemaPermission}>
              <div className="px-6 py-5 border-b border-ui-light-border dark:border-ui-dark-border">
                <h3 className="text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">
                  Add Schema Permission
                </h3>
              </div>
              
              <div className="p-6">
                <div className="mb-4">
                  <label htmlFor="schema_name" className="block text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary mb-1">
                    Schema
                  </label>
                  <select
                    id="schema_name"
                    name="schema_name"
                    required
                    value={addSchemaForm.schema_name}
                    onChange={(e) => handleInputChange(e, setAddSchemaForm)}
                    className="block w-full px-3 py-2 text-ui-light-text-primary dark:text-ui-dark-text-primary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors"
                  >
                    <option value="" disabled>Select a schema</option>
                    {availableSchemas.map(schema => (
                      <option key={schema} value={schema}>{schema}</option>
                    ))}
                  </select>
                </div>
                
                <div className="mb-4">
                  <label htmlFor="permission" className="block text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary mb-1">
                    Permission Level
                  </label>
                  <select
                    id="permission"
                    name="permission"
                    required
                    value={addSchemaForm.permission}
                    onChange={(e) => handleInputChange(e, setAddSchemaForm)}
                    className="block w-full px-3 py-2 text-ui-light-text-primary dark:text-ui-dark-text-primary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors"
                  >
                    <option value="read">Read</option>
                    <option value="write">Write</option>
                    <option value="all">All</option>
                  </select>
                  <p className="mt-1 text-xs text-ui-light-text-muted dark:text-ui-dark-text-muted">
                    Read: View data only. Write: View and modify data. All: Full access.
                  </p>
                </div>
              </div>
              
              <div className="px-6 py-4 bg-ui-light-hover dark:bg-ui-dark-hover border-t border-ui-light-border dark:border-ui-dark-border flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setIsAddSchemaModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary border border-ui-light-border dark:border-ui-dark-border rounded-md hover:bg-ui-light-card dark:hover:bg-ui-dark-card focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white dark:text-ui-dark-text-primary bg-primary-600 dark:bg-primary-700 rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors"
                >
                  Add Permission
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Add Table Permission Modal */}
      {isAddTableModalOpen && (
        <div className="fixed inset-0 z-10 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-ui-light-card dark:bg-ui-dark-card rounded-lg overflow-hidden shadow-xl w-full max-w-lg">
            <form onSubmit={handleAddTablePermission}>
              <div className="px-6 py-5 border-b border-ui-light-border dark:border-ui-dark-border">
                <h3 className="text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">
                  Add Table Permission
                </h3>
              </div>
              
              <div className="p-6">
                <div className="mb-4">
                  <label htmlFor="schema_name" className="block text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary mb-1">
                    Schema
                  </label>
                  <select
                    id="schema_name"
                    name="schema_name"
                    required
                    value={addTableForm.schema_name}
                    onChange={handleSchemaChange}
                    className="block w-full px-3 py-2 text-ui-light-text-primary dark:text-ui-dark-text-primary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors"
                  >
                    <option value="" disabled>Select a schema</option>
                    {availableSchemas.map(schema => (
                      <option key={schema} value={schema}>{schema}</option>
                    ))}
                  </select>
                </div>
                
                <div className="mb-4">
                  <label htmlFor="table_name" className="block text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary mb-1">
                    Table
                  </label>
                  <select
                    id="table_name"
                    name="table_name"
                    required
                    value={addTableForm.table_name}
                    onChange={(e) => handleInputChange(e, setAddTableForm)}
                    className="block w-full px-3 py-2 text-ui-light-text-primary dark:text-ui-dark-text-primary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors disabled:opacity-50"
                    disabled={!selectedSchema}
                  >
                    <option value="" disabled>Select a table</option>
                    {selectedSchema && availableTables[selectedSchema] && 
                      availableTables[selectedSchema].map(table => (
                        <option key={table} value={table}>{table}</option>
                      ))
                    }
                  </select>
                </div>
                
                <div className="mb-4">
                  <label htmlFor="permission" className="block text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary mb-1">
                    Permission Level
                  </label>
                  <select
                    id="permission"
                    name="permission"
                    required
                    value={addTableForm.permission}
                    onChange={(e) => handleInputChange(e, setAddTableForm)}
                    className="block w-full px-3 py-2 text-ui-light-text-primary dark:text-ui-dark-text-primary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors"
                  >
                    <option value="read">Read</option>
                    <option value="write">Write</option>
                    <option value="all">All</option>
                  </select>
                  <p className="mt-1 text-xs text-ui-light-text-muted dark:text-ui-dark-text-muted">
                    Read: View data only. Write: View and modify data. All: Full access.
                  </p>
                </div>
              </div>
              
              <div className="px-6 py-4 bg-ui-light-hover dark:bg-ui-dark-hover border-t border-ui-light-border dark:border-ui-dark-border flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setIsAddTableModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary border border-ui-light-border dark:border-ui-dark-border rounded-md hover:bg-ui-light-card dark:hover:bg-ui-dark-card focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white dark:text-ui-dark-text-primary bg-primary-600 dark:bg-primary-700 rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors"
                >
                  Add Permission
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PermissionManagement;